import { useCallback, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from 'hooks/queryKeys'
import { useAuth } from 'shared/context/AuthContext'
import { getAuthState } from 'shared/utils/authState'
import { useAddTrackingUser } from 'hooks/useAddTrackingUser'
import { useTrackingUserLastQuery } from 'hooks/useTrackingUserLast'
import { formatUtcDate } from 'utils/formatUtcDate'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { isTabDeclaredVisible, pickContent, StoContentTypes, type UpdatesContent } from 'models/projectContent'
import { stoQueries } from './queries/stoQueries'
import { useStoProject } from './useStoProject'

export interface StoUpdate {
  id: string
  title: string
  /** Rich text (HTML) authored in back-office. */
  text: string
  /** `1 Sep 2026`; none when back-office left the date empty. */
  dateLabel?: string
  link?: string
  /** The newest update: the blue dot. */
  isLatest: boolean
  /** Newer than the signed-in visitor's last look at the updates: the dot blinks. */
  isUnseen: boolean
}

/** The tracking-user page name: shared with the legacy page, so a visit there counts here too. */
export const UPDATES_TRACKING_PAGE = 'updates'

/** Without a recorded visit (never looked, or before tracking): the last 100 days count as new, as before. */
const FIRST_VISIT_WINDOW_MS = 100 * 24 * 60 * 60 * 1000

/** Editor leftovers: empty paragraphs at either end of the text would add blank lines. */
const EDGE_EMPTY_PARAGRAPHS = /^(\s*<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>)+|(<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/gi

/** Line breaks closing the last paragraph (`…BTC. <br><br></p>`). */
const TRAILING_BREAKS = /(\s|&nbsp;|<br\s*\/?>)+(<\/p>\s*)$/i

export const trimRichText = (html: string | undefined): string =>
  (html ?? '').replace(EDGE_EMPTY_PARAGRAPHS, '').replace(TRAILING_BREAKS, '$2').trim()

/** Kept in capitals when a title is turned into a sentence. */
const ACRONYMS = new Set([
  'BTC',
  'LBTC',
  'USD',
  'USDT',
  'EUR',
  'CHF',
  'NAV',
  'XAUT',
  'ETF',
  'EU',
  'US',
  'KYC',
  'AML',
  'P2P',
])
/** Names whose spelling is known. */
const NAMES: Record<string, string> = {
  BITFINEX: 'Bitfinex',
  SIDESWAP: 'SideSwap',
  STOKR: 'STOKR',
  BLOCKSTREAM: 'Blockstream',
  LIQUID: 'Liquid',
  STRATEGY: 'Strategy',
}

/**
 * Titles typed in capitals ("SUBSEQUENT ISSUANCE OF STRCst") read as a sentence, like the rest of
 * the page: tickers (mixed case or with digits, or the product's), acronyms and known names stay.
 * Titles already in mixed case are the author's and stay as written.
 */
export const harmonizeTitle = (title: string, symbols: string[] = []): string => {
  const letters = title.replace(/[^A-Za-z]/g, '')
  const capitals = letters.replace(/[^A-Z]/g, '').length
  if (!letters || capitals / letters.length < 0.8) return title
  const keep = new Set(symbols.map((symbol) => symbol.toUpperCase()))
  let first = true
  return title.replace(/[A-Za-z0-9][A-Za-z0-9'’.-]*/g, (word) => {
    const bare = word.replace(/[.'’-]+$/, '').toUpperCase()
    const isFirst = first
    first = false
    if (/[a-z]/.test(word) || /\d/.test(word) || keep.has(bare) || ACRONYMS.has(bare)) return word
    if (NAMES[bare]) return NAMES[bare] + word.slice(bare.length)
    const lower = word.toLowerCase()
    return isFirst ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower
  })
}

const dayTime = (date: string | undefined): number => {
  const time = date ? Date.parse(date) : Number.NaN
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time
}

/**
 * Newest first: by date, then by back-office order (higher = added later). `unseenSince` marks the
 * updates dated after it (signed in only; undefined marks none). Server and first client render
 * pass undefined, so the markup they produce matches.
 */
export const toUpdates = (
  content: UpdatesContent | undefined,
  unseenSince?: number,
  symbols: string[] = [],
): StoUpdate[] => {
  const updates = (content?.updates ?? [])
    .map((entry, index) => ({
      index,
      title: harmonizeTitle(entry.heading?.trim() ?? '', symbols),
      text: trimRichText(entry.text),
      date: entry.eventDate?.trim() || undefined,
      // Empty for an update added without reordering (`Number('')` would be 0): its position then.
      order: String(entry.order ?? '').trim() ? Number(entry.order) : Number.NaN,
      link: entry.link?.trim() || undefined,
    }))
    .filter((entry) => entry.title || entry.text)
    .sort(
      (a, b) =>
        dayTime(b.date) - dayTime(a.date) ||
        (Number.isFinite(b.order) ? b.order : b.index) - (Number.isFinite(a.order) ? a.order : a.index),
    )

  return updates.map((entry, position) => ({
    id: `${entry.date ?? 'undated'}-${entry.index}`,
    title: entry.title,
    text: entry.text,
    dateLabel: formatUtcDate(entry.date),
    link: entry.link,
    isLatest: position === 0,
    isUnseen: unseenSince !== undefined && entry.date !== undefined && dayTime(entry.date) >= unseenSince,
  }))
}

export interface UpdatesView {
  updates: StoUpdate[]
  unseenCount: number
}

/**
 * The product's updates (project-content `updates`). Signed in, the ones newer than the last visit
 * blink until the visitor has seen the section; seeing it (or the full list) records the visit for
 * next time, the same tracking-user record the legacy Updates tab wrote.
 */
export function useUpdates(): {
  state: SectionState<UpdatesView>
  /** Records the visit; only meaningful while `canRecord`. */
  markSeen: () => void
  /** Signed in: seeing the section counts. Watched only then, so signing in on the page still records it. */
  canRecord: boolean
} {
  const { project, projectId } = useStoProject()
  const { user } = useAuth()
  const isSignedIn = getAuthState(user) === 'authenticated'
  const projectName = project?.name
  const contentQuery = useQuery(stoQueries.content(projectId))
  const content = pickContent<UpdatesContent>(contentQuery.data, StoContentTypes.UPDATES)

  const lastVisit = useTrackingUserLastQuery({
    projectName,
    page: UPDATES_TRACKING_PAGE,
    userId: user?._id,
    enabled: isSignedIn,
  })
  // Fixed for this page view once the last visit is known: recording this visit must not stop the
  // blinking while the visitor is still looking. Only in the browser, as the user is.
  const userId = user?._id
  const [now] = useState(Date.now)
  const [since, setSince] = useState<{ userId: string; at: number }>()
  if (isSignedIn && userId && since?.userId !== userId && lastVisit.isSuccess) {
    const date = lastVisit.data?.date ? Date.parse(lastVisit.data.date) : Number.NaN
    setSince({ userId, at: Number.isNaN(date) ? now - FIRST_VISIT_WINDOW_MS : date })
  }
  const unseenSince = isSignedIn && since?.userId === userId ? since?.at : undefined

  const symbol = project?.tokenSymbol?.trim()
  const updates = useMemo(() => toUpdates(content, unseenSince, symbol ? [symbol] : []), [content, unseenSince, symbol])

  // Recorded once per page view; the blinking stays until the next visit, so the visitor sees what was new.
  const queryClient = useQueryClient()
  const { mutate: recordVisit } = useAddTrackingUser()
  // Per user: someone else signing in on the same page records their own visit.
  const recordedFor = useRef<string>(undefined)
  const markSeen = useCallback(() => {
    if (!isSignedIn || !projectName || !userId || recordedFor.current === userId) return
    recordedFor.current = userId
    recordVisit(
      { page: UPDATES_TRACKING_PAGE, projectName },
      {
        // The next page view (another product and back) reads this visit, not the one before it.
        onSuccess: () =>
          queryClient.setQueryData(queryKeys.trackingUserLast(projectName, UPDATES_TRACKING_PAGE, userId), {
            date: new Date().toISOString(),
          }),
      },
    )
  }, [isSignedIn, projectName, userId, recordVisit, queryClient])

  const state = resolveSectionState({
    declaredVisible: isTabDeclaredVisible(content),
    isPending: contentQuery.isPending,
    isError: contentQuery.isError,
    data: { updates, unseenCount: updates.filter((update) => update.isUnseen).length },
    isEmpty: (view) => view.updates.length === 0,
  })

  return { state, markSeen, canRecord: isSignedIn && Boolean(projectName) }
}
